import React, {useEffect, useImperativeHandle, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import StyledInsightsDashboardCard from 'components/organisms/Cards/InsightsDashboardCard/InsightsDashboardCard.styles';
import ToggleButtonGroup from 'components/atoms/Toggles/ToggleButtonGroup/ToggleButtonGroup';
import ToggleButton from 'components/atoms/Toggles/ToggleButton/ToggleButton';
import InsightsList from 'components/organisms/Lists/InsightsList/InsightsList';
import Box from 'components/atoms/Layout/Box/Box';
import {P} from 'components/atoms/Text/Typography/Typography';
import constants from 'helpers/constants';
import {useClientInsightList} from 'services/client/insight/insight.hooks';
import utils from 'helpers/utils';
import {useProfile} from 'components/organisms/Providers/ProfileProvider/ProfileProvider';
import {useAuthClientId} from 'components/organisms/Providers/AuthProvider/AuthProvider';

const InsightsDashboardCard = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'InsightsDashboardCard', {
    children: ['insights']
  });

  const innerRef = useRef(null);
  const clientId = useAuthClientId();
  const profileProvider = useProfile();
  const cardState = profileProvider.cardState;

  useImperativeHandle(ref, () => innerRef.current);

  const showFollowing = Boolean(cardState.cardSettings['insights']?.filter?.showFollowing);
  const insights = useClientInsightList({
    clientId,
    page: 0,
    pageSize: 10,
    filter: (showFollowing ? [{id: 'following', value: true}] : []),
    sort: [{id: 'createdAt', desc: true}]
  }, {
    ...constants.queryOptions.infinite
  });
  const isLoading = insights.status.isLoading || !utils.isDefined(insights.data);

  const setCardSettingsEvent = useEffectEvent(cardState.setCardSettings);

  const onToggle = () => {
    setCardSettingsEvent?.((current) => ({
      ...current,
      'insights': {
        ...current?.insights,
        filter: {
          ...current?.insights?.filter,
          showFollowing: !showFollowing
        }
      }
    }));
  }

  const renderToggleButton = () => {
    return <ToggleButtonGroup color="primary"
                              size="small"
                              value={showFollowing ? 'following' : 'all'}
                              exclusive={true}
                              onChange={onToggle}
                              aria-label="Insights">
      <ToggleButton value="all" color="primary" size="small">All</ToggleButton>
      <ToggleButton value="following" color="primary" size="small">Following</ToggleButton>
    </ToggleButtonGroup>
  };

  useEffect(() => {
    const scroll = () => {
      return innerRef.current?.querySelector('.DataList-list')?.scrollTo({top: 0, left: 0});
    }

    return utils.observeRetry(scroll, 5);
  }, [showFollowing]);

  return <StyledInsightsDashboardCard ref={innerRef} {...innerProps}
                                      title="Insights"
                                      context={renderToggleButton()}>
    {(insights.data?.length > 0 || isLoading) ?
      <InsightsList className="InsightDashboardCard-insights"
                    insights={insights} /> : null}

    {(insights.data?.length === 0 && !isLoading) ?
      <Box className="DashboardCard-empty">
        <P>No insight(s) found</P>
      </Box> : null}
  </StyledInsightsDashboardCard>
});

InsightsDashboardCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ])
};

export default InsightsDashboardCard;
